import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { PlayerProgressItem } from '@/features/player/components/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { CurrentLevelBadgeRow } from './current-level-badge-row';
import { formatDisplayDate, parseLevelNumber } from './progress-tracker.utils';

type PlayerProfileLevelDetailsSheetProps = {
  open: boolean;
  item: PlayerProgressItem | null;
  onOpenChange: (open: boolean) => void;
};

export function PlayerProfileLevelDetailsSheet({
  open,
  item,
  onOpenChange,
}: PlayerProfileLevelDetailsSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const copy = STRINGS.player.profile.progressTrackerContent;

  const levelNumber = useMemo(() => parseLevelNumber(item?.name) ?? 1, [item?.name]);
  const levelColor = useMemo(
    () => colors[`Level ${levelNumber}` as keyof typeof colors] ?? colors['Level 9'],
    [levelNumber],
  );
  const mastered = useMemo(() => formatDisplayDate(item?.dateCompleted), [item?.dateCompleted]);
  const masteredText = useMemo(() => {
    if (!item) return '';
    if (mastered && mastered !== '-') {
      return `${copy.masteredLabel} ${copy.onLabel} ${mastered}`;
    }
    return copy.masteredLabel;
  }, [copy.masteredLabel, copy.onLabel, item, mastered]);

  const closeSheet = () => onOpenChange(false);

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        tabletWidth={scale(375)}
        closeOnBackdropPress={false}
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}>
        <Stack style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity onPress={closeSheet} hitSlop={scale(12)} style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.filters.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {copy.levelDetailsTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          <Stack style={styles.divider} />

          <ScrollView
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            bounces
            alwaysBounceVertical
            keyboardShouldPersistTaps="handled"
            style={styles.scrollView}
            contentContainerStyle={styles.bodyContent}>
            {!item ? null : (
              <Stack direction="row" style={styles.bodyRow}>
                <Stack style={[styles.levelStripe, { backgroundColor: levelColor }]} />
                <Stack flex={1} style={styles.levelContent}>
                  <Stack style={styles.card}>
                    <Stack paddingX={scale(10)} paddingY={scale(12)} gap={scale(4)}>
                      <Text
                        fontWeight={'800'}
                        fontSize={scale(10)}
                        fontFamily="Montserrat"
                        style={[styles.levelTitle, { color: levelColor }]}>
                        {`LEVEL ${levelNumber}`}
                      </Text>
                      <Text
                        fontWeight={'400'}
                        fontSize={scale(8)}
                        fontFamily="Inter"
                        style={styles.subText}>
                        {masteredText}
                      </Text>
                    </Stack>

                    <Stack style={styles.cardDivider} />

                    <Stack>
                      {(item.badges ?? []).map((badge, idx) => (
                        <CurrentLevelBadgeRow
                          key={`${badge.name}-${idx}`}
                          badge={badge}
                          levelColor={levelColor}
                          isLast={idx === (item.badges?.length ?? 0) - 1}
                          context="levelDetails"
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </ScrollView>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale }) => ({
  sheet: {
    backgroundColor: colors['3'],
  },
  sheetPhone: {
    height: '92%',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    paddingBottom: scale(16),
    backgroundColor: colors['0'],
  },
  backButton: {
    width: scale(45),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(45),
  },
  divider: {
    height: 1,
    backgroundColor: colors['3'],
  },
  scrollView: {
    flex: 1,
    backgroundColor: colors['3'],
  },
  bodyContent: {
    flexGrow: 1,
    minHeight: '100%',
    paddingHorizontal: scale(18),
    paddingTop: scale({ phone: 12, tablet: 16 }),
    paddingBottom: scale(28),
  },
  bodyRow: {
    width: '100%',
  },
  levelStripe: {
    width: scale(9),
    borderRadius: scale(2),
  },
  levelContent: {
    paddingLeft: scale(0),
  },
  card: {
    backgroundColor: colors['0'],
    borderTopRightRadius: scale(2),
    borderBottomRightRadius: scale(2),
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['10'],
  },
  levelTitle: {
    textTransform: 'uppercase',
  },
  subText: {
    color: colors['6'],
  },
}));
